import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import logo from '../assets/img/Oman.png';

/**
 * Standalone layout for the Profile page (outside dashboard).
 * Header: Back to Home, Logo, User, Logout.
 */
const ProfileLayout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, isRTL, language, changeLanguage } = useLanguage();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div
      className="min-h-screen flex flex-col text-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-50 border-b border-slate-200 bg-white shadow-sm"
        dir="ltr"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-[#0AB0CE]/50 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-[#111034] shadow-sm hover:bg-[#0AB0CE]/5 transition-all"
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#0AB0CE] to-[#111034] text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-3 w-3"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </span>
            <span className="hidden xs:inline">{t('dashboard.backToHome')}</span>
            <span className="xs:hidden">{t('dashboard.home')}</span>
          </Link>

          <div className="flex items-center gap-3">
            <img src={logo} alt="Logo" className="h-7 w-auto" />
            <div className="hidden sm:block">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                {t('dashboard.myProfile') || 'My Profile'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className={`hidden sm:flex flex-col leading-tight ${isRTL ? 'items-start' : 'items-end'}`}>
              <span className="text-xs font-medium text-slate-900">
                {user?.full_name || user?.email}
              </span>
              <span className="text-[10px] text-slate-500">
                {t('dashboard.welcome')}
              </span>
            </div>
            <button
              onClick={() => changeLanguage(language === 'en' ? 'ar' : 'en')}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0AB0CE]/50 bg-white px-3 py-1.5 text-xs sm:text-sm font-medium text-[#111034] shadow-sm hover:bg-[#0AB0CE]/5 transition-all"
              title={language === 'en' ? 'Switch to Arabic' : 'التبديل إلى الإنجليزية'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span>{language === 'en' ? 'AR' : 'EN'}</span>
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1 rounded-full bg-red-500/90 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-red-500 transition-colors"
            >
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/10" aria-hidden="true">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </span>
              <span>{t('dashboard.logout')}</span>
            </button>
          </div>
        </div>
      </motion.header>

      <main className="flex-1 w-full min-h-0">
        {children}
      </main>
    </div>
  );
};

export default ProfileLayout;
